import React, { useMemo, useState } from "react";
import { FDD_TOOLS, type AnalysisType, type FDDQueueItem } from "../types";
import {
  addToQueue,
  clearQueueByType,
  getFDDQueueByType,
  removeFromQueue,
} from "../store/fddStore";
import SharePointFilePickerButton, {
  type PickedFile,
} from "../../components/SharePointFilePickerButton";
import PdfExtractPanel from "../tools/pdf-extract/PdfExtractPanel";
import DatabookPanel from "../tools/databook/DatabookPanel";
import TbAggregatorPanel from "../tools/tb-aggregator/TbAggregatorPanel";
import ContractReviewPanel from "../tools/contract-review/ContractReviewPanel";
import AgendaPanel from "../tools/agenda/AgendaPanel";
import FeeSummaryPanel from "../tools/fee-summary/FeeSummaryPanel";

type Props = {
  projectId: string;
  folderSelected: boolean;
  projectName?: string;
};

function formatFileSize(bytes?: number): string {
  if (bytes === undefined || bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function statusColor(status: FDDQueueItem["status"]): string {
  switch (status) {
    case "completed": return "var(--theme-action)";
    case "in_progress": return "#3b82f6";
    case "error": return "#dc2626";
    case "pending_upload": return "#b7950b";
    default: return "#6b7280";
  }
}

function statusLabel(status: FDDQueueItem["status"]): string {
  if (status === "pending_upload") return "waiting to upload";
  return status.replace("_", " ");
}

function sourceBadge(item: FDDQueueItem): string {
  if (item.source === "sharepoint") return "SharePoint";
  return "Local";
}

const FDDToolbox: React.FC<Props> = ({ projectId, folderSelected, projectName }) => {
  const [activeTool, setActiveTool] = useState<AnalysisType>(FDD_TOOLS[0].id);
  const [queueVersion, setQueueVersion] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const tool = useMemo(
    () => FDD_TOOLS.find((t) => t.id === activeTool) ?? FDD_TOOLS[0],
    [activeTool]
  );

  const queue = useMemo(
    () => getFDDQueueByType(projectId, activeTool),
    [projectId, activeTool, queueVersion]
  );

  const refresh = () => setQueueVersion((v) => v + 1);

  const queueLocalFiles = (files: File[]) => {
    if (!files.length) return;
    for (const f of files) {
      addToQueue(
        projectId,
        {
          fileId: f.name,
          fileName: f.name,
          fileWebUrl: "",
          fileSize: f.size,
          source: "local",
          analysisType: activeTool,
          projectId,
        },
        f,
      );
    }
    refresh();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    queueLocalFiles(dropped);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    queueLocalFiles(Array.from(input.files || []));
    input.value = "";
  };

  const handleSharePointPick = (picked: PickedFile[]) => {
    for (const p of picked) {
      addToQueue(projectId, {
        fileId: p.id,
        driveId: p.driveId,
        fileName: p.name,
        fileWebUrl: p.webUrl,
        fileSize: p.size,
        source: "sharepoint",
        analysisType: activeTool,
        projectId,
      });
    }
    refresh();
  };

  const handleRemove = (id: string) => {
    removeFromQueue(projectId, id);
    refresh();
  };

  const handleClearAll = () => {
    if (queue.length === 0) return;
    if (!confirm(`Remove all ${queue.length} queued ${tool.name} file${queue.length === 1 ? "" : "s"}?`)) {
      return;
    }
    clearQueueByType(projectId, activeTool);
    refresh();
  };

  return (
    <div style={{ marginTop: 16, display: "flex", gap: 16, alignItems: "stretch" }}>
      {/* Sidebar */}
      <aside
        style={{
          width: 220,
          flexShrink: 0,
          border: "1px solid var(--theme-border-strong)",
          borderRadius: 12,
          background: "var(--theme-tint-7)",
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div style={{ fontWeight: 700, color: "var(--theme-ink)", padding: "4px 6px", fontSize: 13 }}>
          FDD Tools
        </div>
        {FDD_TOOLS.map((t) => {
          const isActive = t.id === activeTool;
          const coming = t.comingSoon === true;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTool(t.id)}
              style={{
                background: isActive ? "var(--theme-accent)" : "#ffffff",
                color: isActive ? "#fff" : coming ? "#6b7280" : "var(--theme-ink)",
                border: `1px solid ${isActive ? "var(--theme-accent)" : "var(--theme-border-strong)"}`,
                borderRadius: 8,
                padding: "10px 12px",
                cursor: "pointer",
                textAlign: "left",
                fontWeight: isActive ? 700 : 500,
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>{t.icon}</span>
              <span style={{ flex: 1 }}>{t.name}</span>
              {coming && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    padding: "2px 6px",
                    borderRadius: 999,
                    background: isActive ? "rgba(255,255,255,0.25)" : "#fef9c3",
                    color: isActive ? "#fff" : "#854d0e",
                    textTransform: "uppercase",
                  }}
                >
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </aside>

      {/* Main area */}
      <main
        style={{
          flex: 1,
          border: "1px solid var(--theme-border-strong)",
          borderRadius: 12,
          background: "#ffffff",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {/* Tool header */}
        <div>
          <h3 style={{ margin: 0, color: "var(--theme-ink)", fontSize: 18, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 22 }}>{tool.icon}</span>
            {tool.name}
          </h3>
          <div style={{ fontSize: 13, color: "var(--theme-ink-soft)", marginTop: 4 }}>{tool.description}</div>
        </div>

        {/* Input section — hidden for tools that handle their own intake or
            haven't been wired up yet. */}
        {activeTool !== "databook" && activeTool !== "agenda" && !tool.ownUploads && !tool.comingSoon && (
        <section
          style={{
            border: "1px solid var(--theme-border)",
            borderRadius: 10,
            background: "var(--theme-tint-3)",
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 13 }}>Inputs</div>

          {/* Action row */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <SharePointFilePickerButton onPicked={handleSharePointPick} />
            <label
              style={{
                background: "#ffffff",
                color: "var(--theme-ink)",
                border: "1px solid var(--theme-border)",
                borderRadius: 8,
                padding: "8px 12px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              📁 Choose Files
              <input type="file" multiple onChange={handleFileInput} style={{ display: "none" }} />
            </label>
            {!folderSelected && (
              <span style={{ fontSize: 12, color: "#b7950b" }}>
                ⚠️ Select a project folder above to enable SharePoint picking.
              </span>
            )}
          </div>

          {/* Drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            style={{
              padding: 24,
              border: `2px dashed ${dragOver ? "var(--theme-action)" : "var(--theme-accent)"}`,
              background: dragOver ? "var(--theme-tint-21)" : "var(--theme-tint-7)",
              color: "var(--theme-ink)",
              borderRadius: 10,
              textAlign: "center",
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Drag files here to queue for {tool.name}
              {activeTool === "pdf-extract" && " — or paste a snip with Ctrl+V"}
            </div>
            <div style={{ fontSize: 12, color: "var(--theme-ink-soft)" }}>
              Files are queued locally and ready for analysis.
              {activeTool === "pdf-extract" && " Snipping Tool clips paste directly."}
            </div>
          </div>

          {/* Queued files */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <div style={{ fontWeight: 600, color: "var(--theme-ink)", fontSize: 13 }}>
                Queued Files ({queue.length})
              </div>
              {queue.length > 0 && (
                <button
                  onClick={handleClearAll}
                  title="Remove every file from this tool's queue"
                  style={{
                    background: "#fef2f2",
                    color: "#dc2626",
                    border: "1px solid #fca5a5",
                    borderRadius: 6,
                    padding: "4px 10px",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Clear All
                </button>
              )}
            </div>
            {queue.length === 0 ? (
              <div
                style={{
                  fontSize: 12,
                  color: "#6b7280",
                  fontStyle: "italic",
                  padding: 8,
                  background: "#ffffff",
                  border: "1px dashed var(--theme-border)",
                  borderRadius: 8,
                  textAlign: "center",
                }}
              >
                No files queued yet for {tool.name}.
              </div>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                {queue.map((item) => {
                  const isPendingUpload = item.status === "pending_upload";
                  return (
                  <li
                    key={item.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 10px",
                      background: isPendingUpload ? "#f5f5f5" : "#ffffff",
                      border: `1px solid ${isPendingUpload ? "#e5e5e5" : "var(--theme-tint-17)"}`,
                      borderRadius: 8,
                      fontSize: 12,
                      opacity: isPendingUpload ? 0.7 : 1,
                    }}
                  >
                    <span style={{ flex: 1, fontFamily: "monospace", color: isPendingUpload ? "#9ca3af" : "#333", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.fileName}
                    </span>
                    <span style={{ width: 70, fontSize: 11, color: "#6b7280" }}>
                      {sourceBadge(item)}
                    </span>
                    <span style={{ width: 80, color: "#666", textAlign: "right" }}>
                      {formatFileSize(item.fileSize)}
                    </span>
                    <span
                      style={{
                        width: 130,
                        textAlign: "center",
                        textTransform: "capitalize",
                        color: statusColor(item.status),
                        fontWeight: 600,
                      }}
                    >
                      {statusLabel(item.status)}
                    </span>
                    <button
                      onClick={() => handleRemove(item.id)}
                      title="Remove from queue"
                      style={{
                        background: "transparent",
                        color: "#dc2626",
                        border: "1px solid #fca5a5",
                        borderRadius: 6,
                        padding: "2px 8px",
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                    >
                      Remove
                    </button>
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
        )}

        {/* Tool-specific section */}
        {activeTool === "pdf-extract" ? (
          <PdfExtractPanel
            key={projectId}
            projectId={projectId}
            projectName={projectName}
            queueVersion={queueVersion}
            onQueueChanged={refresh}
          />
        ) : activeTool === "databook" ? (
          <DatabookPanel projectId={projectId} projectName={projectName} />
        ) : activeTool === "tb-aggregator" ? (
          <TbAggregatorPanel key={projectId} projectId={projectId} projectName={projectName} />
        ) : activeTool === "agenda" ? (
          <AgendaPanel projectId={projectId} projectName={projectName} />
        ) : activeTool === "contract-review" ? (
          <ContractReviewPanel
            projectId={projectId}
            projectName={projectName}
            queueVersion={queueVersion}
            onQueueChanged={refresh}
          />
        ) : activeTool === "fee-summary" ? (
          <FeeSummaryPanel key={projectId} projectId={projectId} projectName={projectName} />
        ) : tool.comingSoon ? (
          <section
            style={{
              border: "1px solid #f5d97e",
              borderRadius: 10,
              background: "#fffbeb",
              padding: 32,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 8,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 32 }}>{tool.icon}</div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.6,
                padding: "3px 10px",
                borderRadius: 999,
                background: "#fef3c7",
                color: "#854d0e",
                textTransform: "uppercase",
              }}
            >
              Coming soon
            </div>
            <div style={{ fontWeight: 700, color: "#854d0e", fontSize: 15 }}>
              {tool.name} isn't ready yet
            </div>
            <div style={{ color: "#92651e", fontSize: 13, maxWidth: 460 }}>
              {tool.description}. We've reserved the spot so you know it's on the way — check back once
              the core IRL/Databook flow is fully landed and we'll wire this one up next.
            </div>
          </section>
        ) : (
          <section
            style={{
              border: "1px solid var(--theme-border)",
              borderRadius: 10,
              background: "var(--theme-tint-3)",
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <div style={{ fontWeight: 700, color: "var(--theme-ink)", fontSize: 13 }}>Output</div>
            <div
              style={{
                padding: 24,
                background: "#ffffff",
                border: "1px dashed var(--theme-border)",
                borderRadius: 10,
                textAlign: "center",
                color: "#6b7280",
                fontSize: 13,
              }}
            >
              <div style={{ fontWeight: 600, color: "var(--theme-ink-soft)", marginBottom: 4 }}>
                {tool.icon} {tool.name} output will appear here
              </div>
              <div style={{ fontSize: 12 }}>
                Analysis results, downloads, and previews are not yet wired up.
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default FDDToolbox;
